import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const JAN_2026 = new Date("2025-01-01T00:00:00");
const MIN_WORKSHOPS = 5;

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const compactPhone = (phone) => String(phone || "").replace(/\D/g, "").slice(-10);
const cleanText = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const throttledAll = async (tasks, concurrency = 50) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += concurrency) {
        const batch = await Promise.all(tasks.slice(i, i + concurrency).map(t => t()));
        results.push(...batch);
    }
    return results;
};

const batchedIn = async (collName, field, values) => {
    const { query, where } = await import("firebase/firestore");
    const clean = [...new Set(values.filter(Boolean))];
    if (!clean.length) return [];
    const chunks = [];
    for (let i = 0; i < clean.length; i += 10) chunks.push(clean.slice(i, i + 10));
    const snaps = await throttledAll(
        chunks.map(chunk => () => getDocs(query(collection(db, collName), where(field, "in", chunk)))),
        50
    );
    return snaps.flatMap(s => s.docs);
};

function NonGlowWorkshopAttendees() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // 1. Build phone sets (Glow members + udhyogini_features)
                const [glowSnap, udhyoginiSnap, funderSnap, shikshaSnap] = await Promise.all([
                    getDocs(collection(db, "nv_glow_membership")),
                    getDocs(collection(db, "udhyogini_features")),
                    getDocs(collection(db, "funder_users_list")),
                    getDocs(collection(db, "nv_shiksha_subscriptions")),
                ]);
                const glowPhones = new Map(); // phone -> end_date
                const excludedPhones = new Set();
                const today = new Date();
                glowSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!phone || phone.length < 10) return;
                    const endDate = parseDate(d.end_date);
                    // keep the latest end_date if multiple records for same phone
                    if (!glowPhones.has(phone) || (endDate && endDate > glowPhones.get(phone))) {
                        glowPhones.set(phone, endDate);
                    }
                    excludedPhones.add(phone);
                });
                udhyoginiSnap.forEach(docSnap => {
                    const phone = compactPhone(docSnap.data().phone);
                    if (phone && phone.length >= 10) excludedPhones.add(phone);
                });

                // Build funder set: phone → U+
                const funderSet = new Set();
                funderSnap.forEach(docSnap => {
                    const phone = compactPhone(docSnap.data().phone_no || docSnap.id);
                    if (phone) funderSet.add(phone);
                });

                // Build shiksha active map: phone → true/false
                const shikshaActiveMap = {};
                shikshaSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (!phone) return;
                    const end = parseDate(d.end_date);
                    const isActive = end && end > today;
                    if (isActive) shikshaActiveMap[phone] = true;
                });

                // 2. Workshop registrations — fetch all, filter Jan 2026+ client-side
                const wsSnap = await getDocs(collection(db, "workshop_registration"));
                const phoneCounts = {};
                const phoneWorkshops = {};

                wsSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const dt = parseDate(d.created_date);
                    if (!dt || dt < JAN_2026) return;
                    const phone = compactPhone(d.phone);
                    if (!phone || phone.length < 10) return;

                    phoneCounts[phone] = (phoneCounts[phone] || 0) + 1;
                    if (!phoneWorkshops[phone]) phoneWorkshops[phone] = [];
                    phoneWorkshops[phone].push({
                        name: d.workshop_event_name || "-",
                        desc: d.description || "-",
                        date: d.workshop_Date || d.created_date,
                        location: d.workshop_location || "-",
                        payment: d.payment || "-",
                    });
                });

                // 3. Filter: 5+ workshops (show all, glow status shown in column)
                const qualifiedPhones = Object.keys(phoneCounts)
                    .filter(p => phoneCounts[p] >= MIN_WORKSHOPS);

                if (qualifiedPhones.length === 0) {
                    setRows([]);
                    return;
                }

                // 4. Join with personal_details for user info
                const userDocs = await batchedIn("personal_details", "phone_no", qualifiedPhones);
                const userMap = {};
                userDocs.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (phone) userMap[phone] = d;
                });

                // 5. Build result rows, sort by workshop count desc
                const result = qualifiedPhones.map(phone => {
                    const u = userMap[phone] || {};
                    return {
                        phone,
                        name: cleanText(u.full_name || u.name),
                        email: cleanText(u.email),
                        username: cleanText(u.username),
                        type: cleanText(u.type),
                        joinedDate: u.created_date || null,
                        workshopCount: phoneCounts[phone],
                        isGlowActive: glowPhones.has(phone) && glowPhones.get(phone) > today,
                        isShikshaActive: shikshaActiveMap[phone] || false,
                        isUPlus: funderSet.has(phone),
                        workshops: (phoneWorkshops[phone] || []).sort((a, b) =>
                            (parseDate(b.date)?.getTime() || 0) - (parseDate(a.date)?.getTime() || 0)
                        ),
                    };
                }).sort((a, b) => b.workshopCount - a.workshopCount);

                setRows(result);
            } catch (err) {
                console.error("NonGlowWorkshopAttendees fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const handleExport = () => {
        // Tab 1 — user summary
        const summary = rows.map((r, i) => ({
            "#": i + 1,
            Name: r.name,
            Phone: r.phone,
            Username: r.username,
            "Workshops Attended (Jan 2025+)": r.workshopCount,
            // "Glow Status":                    r.isGlowActive ? "Active" : "Not Member",
            "Shiksha Member": r.isShikshaActive ? "Active" : "Not Member",
            "User Type": r.isUPlus ? "U+" : "Normal User",
            "Joined Date": fmtDate(r.joinedDate),
        }));

        // Tab 2 — one row per workshop registration
        const details = [];
        rows.forEach(r => {
            r.workshops.forEach((w, wi) => {
                details.push({
                    Phone: r.phone,
                    Name: r.name,
                    Username: r.username,
                    "#": wi + 1,
                    "Workshop Name": w.name,
                    "Description": w.desc,
                    "Workshop Date": fmtDate(w.date),
                    "Location": w.location,
                    "Payment": w.payment,
                });
            });
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Users Summary");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(details), "Workshop Details");
        XLSX.writeFile(wb, "non_glow_5plus_workshops.xlsx");
    };

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Non-Glow Members · 5+ Workshops</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            Users <strong>not</strong> on Glow membership who attended ≥ 5 workshops since Jan 2025
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                {/* SUMMARY CARD */}
                {!loading && (
                    <div className="row g-3 mb-3">
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#4361ee" }}>{rows.length}</div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>Users with {MIN_WORKSHOPS}+ Workshops (Jan 2025+)</div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#0d9488" }}>
                                    {rows.reduce((s, r) => s + r.workshopCount, 0)}
                                </div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>Total Workshop Attendances</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <span className="fw-bold" style={{ fontSize: "14px" }}>User List</span>
                            <div className="d-flex gap-2 align-items-center">
                                {/* <span className="badge bg-secondary" style={{ fontSize: "12px", padding: "6px 10px" }}>
                                    {rows.length} users
                                </span> */}
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={handleExport}
                                    disabled={rows.length === 0}
                                >
                                    Export Excel
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table
                                className="table table-bordered table-hover align-middle mb-0"
                                style={{ fontSize: "13px" }}
                            >
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Username</th>
                                        <th className="text-center">Workshops (Jan 2025+)</th>
                                        {/* <th className="text-center">Glow Status</th> */}
                                        <th className="text-center">Shiksha/Glow Member</th>
                                        <th className="text-center">User Type</th>
                                        <th>Joined Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-4 text-muted">
                                                No non-Glow users found with {MIN_WORKSHOPS}+ workshops since Jan 2026
                                            </td>
                                        </tr>
                                    ) : rows.map((row, i) => (
                                        <tr key={row.phone}>
                                            <td>{i + 1}</td>
                                            <td>{row.name}</td>
                                            <td>{row.phone}</td>
                                            <td>{row.username}</td>
                                            <td className="text-center">
                                                <span
                                                    className="badge bg-primary"
                                                    style={{ cursor: "pointer", fontSize: "13px", padding: "5px 10px" }}
                                                    onClick={() => setModal(row)}
                                                    title="Click to see workshops"
                                                >
                                                    {row.workshopCount}
                                                </span>
                                            </td>
                                            {/* <td className="text-center">
                                                <span className={`badge ${row.isGlowActive ? "bg-success" : "bg-secondary"}`}
                                                    style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                    {row.isGlowActive ? "✓ Active" : "Not Member"}
                                                </span>
                                            </td> */}
                                            <td className="text-center">
                                                <span className={`badge ${row.isShikshaActive ? "bg-success" : "bg-secondary"}`}
                                                    style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                    {row.isShikshaActive ? "✓ Active" : "Not Member"}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge ${row.isUPlus ? "bg-warning text-dark" : "bg-primary"}`}
                                                    style={{ fontSize: "12px", padding: "5px 10px", fontWeight: 700 }}>
                                                    {row.isUPlus ? "U+" : "Normal User"}
                                                </span>
                                            </td>
                                            <td>{fmtDate(row.joinedDate)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* WORKSHOP DETAIL MODAL */}
            {modal && (
                <div
                    onClick={() => setModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "600px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    Workshops Attended ({modal.workshopCount})
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {modal.name} · {modal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr><th>#</th><th>Workshop</th><th>Description</th><th>Workshop Date</th><th>Location</th><th>Payment</th></tr>
                                </thead>
                                <tbody>
                                    {modal.workshops.map((w, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{w.name}</td>
                                            <td>{w.desc}</td>
                                            <td>{fmtDate(w.date)}</td>
                                            <td>{w.location}</td>
                                            <td>{w.payment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default NonGlowWorkshopAttendees;
